import authors from '../authors.json';
import categories from '../categories.json';

// blog-index.json and blog-content/<slug>.json are generated by
// scripts/build-content.js — see that file for why content isn't bundled
// into the app's JS at all (it wouldn't scale past a few dozen posts
// without hurting every listing page's load time). The index is fetched
// once per page load and cached here in module scope for the session.
let indexPromise = null;

function fetchIndex() {
  if (!indexPromise) {
    indexPromise = fetch('/blog-index.json')
      .then(res => (res.ok ? res.json() : []))
      .catch(() => []);
  }
  return indexPromise;
}

export function getAuthor(slug) {
  return authors[slug] || null;
}

export function getCategory(slug) {
  return categories[slug] || null;
}

export function getAllCategories() {
  return Object.values(categories);
}

export function getAllAuthors() {
  return Object.values(authors);
}

export async function getAllPosts() {
  return fetchIndex();
}

// Only ever returns posts explicitly marked `featured: true` — no
// fallback to "just the newest N", which would otherwise show the same
// posts twice (once here, once in the Latest list right below it) on
// every blog home page until an author starts using the flag.
export async function getFeaturedPosts(limit = 3) {
  const posts = await fetchIndex();
  return posts.filter(p => p.featured).slice(0, limit);
}

export async function getPostMeta(slug) {
  const posts = await fetchIndex();
  return posts.find(p => p.slug === slug) || null;
}

export async function getPostsByCategory(categorySlug) {
  const posts = await fetchIndex();
  return posts.filter(p => p.category === categorySlug);
}

export async function getPostsByTag(tagSlug) {
  const posts = await fetchIndex();
  return posts.filter(p => p.tags.includes(tagSlug));
}

export async function getPostsByAuthor(authorSlug) {
  const posts = await fetchIndex();
  return posts.filter(p => p.author === authorSlug);
}

export async function getAllTags() {
  const posts = await fetchIndex();
  const counts = new Map();
  posts.forEach(p => p.tags.forEach(t => counts.set(t, (counts.get(t) || 0) + 1)));
  return [...counts.entries()].map(([slug, count]) => ({ slug, count })).sort((a, b) => b.count - a.count);
}

// +2 for a shared category, +1 per shared tag — simple, deterministic,
// and the exact mechanism that keeps articles from becoming orphans as
// the library grows: every post surfaces others like it automatically.
export async function getRelatedPosts(post, limit = 3) {
  const posts = await fetchIndex();
  return posts
    .filter(p => p.slug !== post.slug)
    .map(p => {
      const sharedTags = p.tags.filter(t => post.tags.includes(t)).length;
      const score = (p.category === post.category ? 2 : 0) + sharedTags;
      return { post: p, score };
    })
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score || (a.post.date < b.post.date ? 1 : -1))
    .slice(0, limit)
    .map(x => x.post);
}

export async function getAdjacentPosts(slug) {
  const posts = await fetchIndex(); // pre-sorted newest-first by the build script
  const idx = posts.findIndex(p => p.slug === slug);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: posts[idx + 1] || null, // older
    next: posts[idx - 1] || null, // newer
  };
}

export function searchPosts(posts, query) {
  const q = query.trim().toLowerCase();
  if (!q) return posts;
  return posts.filter(p =>
    p.title.toLowerCase().includes(q) ||
    p.excerpt.toLowerCase().includes(q) ||
    p.tags.some(t => t.includes(q))
  );
}

export function paginate(items, page = 1, perPage = 10) {
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * perPage;
  return {
    items: items.slice(start, start + perPage),
    page: safePage,
    totalPages,
    total: items.length,
  };
}

export async function fetchPostContent(slug) {
  const res = await fetch(`/blog-content/${slug}.json`);
  if (!res.ok) return null;
  return res.json();
}
